// Editable Cross-Project Gantt — replaces read-only PccGantt.
// Features: drag-to-move, drag-to-resize, click-to-edit name, add/delete
// phases, zoom, today marker, phase completion toggle, persistence via
// /api/pcc/programs/:id/phases endpoints.
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, ZoomIn, ZoomOut, Plus, Check, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../App";
import { fetchJson, fmtDate, fmtDateShort, fmtMoneyShort, healthBadge } from "./pccHelpers";

const DAY_MS = 24 * 60 * 60 * 1000;
const PHASE_COLORS = ["#8FA79B", "#8FA79B", "#B96A43", "#2E7D55", "#C24A2E", "#3D6355", "#8FA79B", "#B96A43"];
// Tag system per PRD v2 — drives bar color + deliverable matching.
const PHASE_TAGS = [
  { id: "kickoff",   label: "Kickoff",   color: "#8FA79B" },
  { id: "buffer",    label: "Buffer",    color: "#8FA79B" },
  { id: "modeling",  label: "Modeling",  color: "#B96A43" },
  { id: "rendering", label: "Rendering", color: "#8FA79B" },
  { id: "animation", label: "Animation", color: "#2E7D55" },
];

function isoDate(d) { return new Date(d).toISOString().slice(0, 10); }
function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }

export default function PccGanttEditable() {
  const [data, setData] = useState(null);
  const [phasesByProgram, setPhasesByProgram] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(6); // px/day
  const [editingPhaseId, setEditingPhaseId] = useState(null);
  const [editingName, setEditingName] = useState("");

  // Load: gantt aggregator + phases per program
  const refresh = useCallback(async () => {
    try {
      const d = await fetchJson("/planning-command-center/gantt");
      setData(d);
      const phaseMap = {};
      await Promise.all((d.programs || []).map(async ({ program }) => {
        try {
          const ps = await fetchJson(`/pcc/programs/${program.id}/phases`);
          phaseMap[program.id] = ps;
        } catch { phaseMap[program.id] = []; }
      }));
      setPhasesByProgram(phaseMap);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Compute global timeline range
  const { rangeStart, totalDays, monthTicks } = useMemo(() => {
    if (!data?.programs?.length) return { rangeStart: new Date(), totalDays: 120, monthTicks: [] };
    let min = Infinity, max = -Infinity;
    for (const { program } of data.programs) {
      [program?.start_window, program?.end_window].filter(Boolean).forEach(s => {
        const t = new Date(s).getTime();
        if (!isNaN(t)) { if (t < min) min = t; if (t > max) max = t; }
      });
    }
    Object.values(phasesByProgram).flat().forEach(p => {
      const s = new Date(p.start_date).getTime();
      const e = s + (p.duration_days || 1) * DAY_MS;
      if (s < min) min = s; if (e > max) max = e;
    });
    if (min === Infinity) { min = Date.now() - 30 * DAY_MS; max = Date.now() + 120 * DAY_MS; }
    const start = new Date(min - 7 * DAY_MS); start.setHours(0, 0, 0, 0);
    const end = new Date(max + 30 * DAY_MS);
    const days = Math.max(90, Math.ceil((end.getTime() - start.getTime()) / DAY_MS));
    const ticks = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cursor.getTime() < end.getTime()) {
      ticks.push({ label: cursor.toLocaleDateString("en-US", { month: "short", year: "2-digit" }), offset: Math.floor((cursor.getTime() - start.getTime()) / DAY_MS) });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return { rangeStart: start, totalDays: days, monthTicks: ticks };
  }, [data, phasesByProgram]);

  const px = zoom;
  const totalWidth = totalDays * px;
  const todayOffset = Math.floor((Date.now() - rangeStart.getTime()) / DAY_MS);

  const offsetFor = useCallback((dateStr) => {
    if (!dateStr) return null;
    const t = new Date(dateStr).getTime();
    if (isNaN(t)) return null;
    return Math.floor((t - rangeStart.getTime()) / DAY_MS) * px;
  }, [rangeStart, px]);

  // ── Drag/resize ──
  const dragRef = useRef(null);
  function startDrag(phase, mode, e) {
    e.stopPropagation();
    dragRef.current = {
      phase, mode,
      startX: e.clientX,
      origStart: new Date(phase.start_date).getTime(),
      origDur: phase.duration_days,
    };
    const onMove = (ev) => {
      const d = dragRef.current; if (!d) return;
      const deltaDays = Math.round((ev.clientX - d.startX) / px);
      setPhasesByProgram(prev => {
        const list = prev[d.phase.program_id] || [];
        const idx = list.findIndex(p => p.id === d.phase.id);
        if (idx < 0) return prev;
        const updated = { ...list[idx] };
        if (d.mode === "move") {
          updated.start_date = isoDate(d.origStart + deltaDays * DAY_MS);
        } else if (d.mode === "resize-end") {
          updated.duration_days = Math.max(1, d.origDur + deltaDays);
        } else if (d.mode === "resize-start") {
          const newStart = d.origStart + deltaDays * DAY_MS;
          const newDur = Math.max(1, d.origDur - deltaDays);
          updated.start_date = isoDate(newStart);
          updated.duration_days = newDur;
        }
        const newList = [...list]; newList[idx] = updated;
        return { ...prev, [d.phase.program_id]: newList };
      });
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const d = dragRef.current; if (!d) return;
      dragRef.current = null;
      // persist
      const list = (await new Promise(r => setPhasesByProgram(prev => { r(prev); return prev; })))[d.phase.program_id];
      const final = list?.find(p => p.id === d.phase.id);
      if (final && (final.start_date !== isoDate(d.origStart) || final.duration_days !== d.origDur)) {
        try {
          await apiClient.patch(`/pcc/phases/${d.phase.id}`, { start_date: final.start_date, duration_days: final.duration_days });
        } catch (er) { toast.error("Failed to save phase"); refresh(); }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  // ── CRUD ──
  async function addPhase(programId) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const list = phasesByProgram[programId] || [];
    const body = {
      program_id: programId,
      name: "New Phase",
      start_date: isoDate(today),
      duration_days: 10,
      color: PHASE_COLORS[list.length % PHASE_COLORS.length],
      sort_order: list.length,
    };
    try {
      const res = await apiClient.post(`/pcc/programs/${programId}/phases`, body);
      setPhasesByProgram(prev => ({ ...prev, [programId]: [...(prev[programId] || []), res.data] }));
    } catch (e) { toast.error("Failed to add phase"); }
  }
  async function deletePhase(phase) {
    if (!window.confirm(`Delete phase "${phase.name}"?`)) return;
    try {
      await apiClient.delete(`/pcc/phases/${phase.id}`);
      setPhasesByProgram(prev => ({ ...prev, [phase.program_id]: (prev[phase.program_id] || []).filter(p => p.id !== phase.id) }));
    } catch { toast.error("Failed to delete"); }
  }
  async function toggleComplete(phase) {
    try {
      const res = await apiClient.patch(`/pcc/phases/${phase.id}`, { is_complete: !phase.is_complete });
      setPhasesByProgram(prev => ({ ...prev, [phase.program_id]: (prev[phase.program_id] || []).map(p => p.id === phase.id ? res.data : p) }));
    } catch { toast.error("Failed"); }
  }
  async function commitName(phase) {
    const name = editingName.trim();
    setEditingPhaseId(null);
    if (!name || name === phase.name) return;
    try {
      await apiClient.patch(`/pcc/phases/${phase.id}`, { name });
      setPhasesByProgram(prev => ({ ...prev, [phase.program_id]: (prev[phase.program_id] || []).map(p => p.id === phase.id ? { ...p, name } : p) }));
    } catch { toast.error("Failed to rename"); }
  }

  async function cycleTag(phase) {
    const idx = PHASE_TAGS.findIndex(t => t.id === (phase.deliverable_tag || ""));
    const next = PHASE_TAGS[(idx + 1) % PHASE_TAGS.length];
    try {
      const res = await apiClient.patch(`/pcc/phases/${phase.id}`, { deliverable_tag: next.id, color: next.color });
      setPhasesByProgram(prev => ({ ...prev, [phase.program_id]: (prev[phase.program_id] || []).map(p => p.id === phase.id ? res.data : p) }));
    } catch { toast.error("Failed"); }
  }

  if (loading) return <div className="flex items-center justify-center py-20 text-white/50"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
  if (error) return <Card className="bg-red-500/[0.06] border-red-500/30 p-4 text-red-200">{error}</Card>;

  return (
    <div className="space-y-4" data-testid="pcc-gantt">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-light text-white">Cross-Project Gantt</h2>
          <p className="text-white/55 text-sm">Drag bars to move · drag edges to resize · double-click name to rename · click ✓ to mark done · click tag chip to cycle.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {PHASE_TAGS.map(t => (
              <div key={t.id} className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">
                <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                <span className="text-[10px] uppercase tracking-wider text-white/65 font-mono">{t.label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1 px-2 py-1 bg-black/30 border border-white/10 rounded">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.max(2, z - 1))} data-testid="pcc-gantt-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
            <span className="text-[11px] font-mono text-white/55 min-w-[16px] text-center">{zoom}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.min(20, z + 1))} data-testid="pcc-gantt-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <Card className="bg-[#000000] border-white/[0.08] p-0 overflow-hidden">
        <div className="grid sticky top-0 bg-[#000000] z-10" style={{ gridTemplateColumns: "280px 1fr" }}>
          <div className="border-r border-white/[0.08] bg-white/[0.02] px-4 py-3 border-b border-white/[0.08]">
            <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Project</div>
          </div>
          <div className="border-b border-white/[0.08] overflow-x-auto">
            <div style={{ width: totalWidth, position: "relative", height: 40 }}>
              {monthTicks.map((t, i) => (
                <div key={i} className="absolute top-0 h-full border-l border-white/[0.06] text-[10px] text-white/40 font-mono pl-1 pt-2" style={{ left: t.offset * px }}>
                  {t.label}
                </div>
              ))}
              {todayOffset >= 0 && todayOffset <= totalDays && (
                <div className="absolute top-0 bottom-0 border-l border-amber-400/60" style={{ left: todayOffset * px }}>
                  <span className="absolute top-1 left-1 text-[9px] text-amber-300 font-mono">today</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-h-[640px] overflow-y-auto">
          {(data?.programs || []).map(({ program, milestones }) => {
            const hb = healthBadge(program.risk_level);
            const phases = phasesByProgram[program.id] || [];
            return (
              <div key={program.id} className="grid border-b border-white/[0.06]" style={{ gridTemplateColumns: "280px 1fr" }}>
                <div className="px-4 py-3 border-r border-white/[0.08]">
                  <Link to={`/planning-command-center/projects/${program.id}`} className="text-sm font-semibold text-white hover:text-amber-300 truncate block">{program.program_name}</Link>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Badge className={`text-[10px] border ${hb.cls}`}>{hb.label}</Badge>
                    <span className="text-[10px] text-white/45 font-mono">{program.stage}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] text-white/40 font-mono">{fmtMoneyShort(program.contract_value)}</span>
                    <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-amber-300 hover:bg-amber-500/10" onClick={() => addPhase(program.id)} data-testid={`pcc-gantt-add-phase-${program.id}`}>
                      <Plus className="w-3 h-3 mr-0.5" />Phase
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className="relative" style={{ width: totalWidth, height: Math.max(64, 24 + phases.length * 30) }}>
                    {monthTicks.map((t, i) => (
                      <div key={i} className="absolute top-0 bottom-0 border-l border-white/[0.04]" style={{ left: t.offset * px }} />
                    ))}
                    {todayOffset >= 0 && todayOffset <= totalDays && (
                      <div className="absolute top-0 bottom-0 border-l border-amber-400/40" style={{ left: todayOffset * px }} />
                    )}
                    {/* Phase bars */}
                    {phases.map((phase, idx) => {
                      const left = offsetFor(phase.start_date);
                      const width = Math.max(8, (phase.duration_days || 1) * px);
                      if (left == null) return null;
                      const top = 14 + idx * 30;
                      const editing = editingPhaseId === phase.id;
                      return (
                        <div key={phase.id} className="absolute select-none" style={{ left, top, width, height: 22 }}>
                          <div
                            className="absolute inset-0 rounded shadow group cursor-move"
                            style={{ background: phase.color || "#8FA79B", opacity: phase.is_complete ? 0.55 : 1 }}
                            onMouseDown={(e) => { if (e.target.dataset?.resize) return; startDrag(phase, "move", e); }}
                            onDoubleClick={() => { setEditingPhaseId(phase.id); setEditingName(phase.name); }}
                            title={`${phase.name} · ${fmtDateShort(phase.start_date)} → ${fmtDateShort(addDays(phase.start_date, phase.duration_days))} · ${phase.duration_days}d`}
                            data-testid={`pcc-gantt-phase-${phase.id}`}
                          >
                            {/* left resize handle */}
                            <div data-resize="start" onMouseDown={(e) => startDrag(phase, "resize-start", e)} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40" />
                            {/* right resize handle */}
                            <div data-resize="end" onMouseDown={(e) => startDrag(phase, "resize-end", e)} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40" />
                            {editing ? (
                              <input
                                autoFocus
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={() => commitName(phase)}
                                onKeyDown={(e) => { if (e.key === "Enter") commitName(phase); if (e.key === "Escape") setEditingPhaseId(null); }}
                                className="absolute inset-y-0 left-2 right-12 bg-transparent text-[11px] font-semibold text-white outline-none border-b border-white"
                              />
                            ) : (
                              <span className="absolute inset-y-0 left-2 right-12 flex items-center text-[11px] font-semibold text-white truncate">{phase.name}</span>
                            )}
                            {/* Actions */}
                            <button onClick={(e) => { e.stopPropagation(); cycleTag(phase); }} className="absolute right-12 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 px-1.5 h-4 rounded-sm bg-black/40 flex items-center" title={`Tag: ${phase.deliverable_tag || "none"} (click to cycle)`}>
                              <span className="text-[8px] uppercase tracking-wider font-mono text-white">{phase.deliverable_tag || "tag"}</span>
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); toggleComplete(phase); }} className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 w-4 h-4 rounded-sm bg-black/40 flex items-center justify-center" title="Toggle done">
                              <Check className="w-3 h-3 text-white" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); deletePhase(phase); }} className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 w-4 h-4 rounded-sm bg-black/40 flex items-center justify-center" title="Delete">
                              <Trash2 className="w-3 h-3 text-red-300" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {/* Milestones from payment_milestones */}
                    {(milestones || []).map(m => {
                      const off = offsetFor(m.date);
                      if (off == null) return null;
                      const overdue = (m.status || "").toLowerCase() !== "paid" && new Date(m.date).getTime() < Date.now();
                      const color = overdue ? "bg-red-400 border-red-200" : (m.status === "paid" ? "bg-emerald-400 border-emerald-200" : "bg-amber-400 border-amber-200");
                      return (
                        <div key={m.id} className={`absolute w-3 h-3 rotate-45 border ${color}`} style={{ left: off - 6, top: 4 }} title={`${m.title} · ${fmtDateShort(m.date)} · ${fmtMoneyShort(m.amount)}`} />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
          {(!data?.programs || data.programs.length === 0) && (
            <div className="px-6 py-10 text-center text-white/40 text-sm">No programs to display.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
